"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react2 = require("@linaria/react");
var _reactIntersectionObserver = require("react-intersection-observer");
var _movements = require("./movements");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/* eslint-disable max-len */

const StaggeredContainer = /*#__PURE__*/(0, _react2.styled)(_movements.StaggerAnimationType)({
  name: "StaggeredContainer",
  class: "pl-s1n14xk8"
});
const Container = /*#__PURE__*/(0, _react2.styled)(_movements.AnimationType)({
  name: "Container",
  class: "pl-cn1upg4"
});

/**
 * MotionMove Component.
 *  * @example
 * ```js
 * <MotionMove
 *    mode="fluid"
 *    intensity="400"
 *    intensityMultiplier={4}
 *    variant="appear"
 *    triggerOnce
 *    staggerChildren
 * >
 *    <div>Animated Div</div>
 * </MotionMove>
 * ```
 *
 * @param {Object} props React props
 * @param {Node} props.children React Children Node
 * @param {Object} props.style Container Style
 * @param {String} props.className Container Class
 * @param {String} props.dataCy Container Data Cy
 * @param {Boolean} props.triggerOnce Animation Trigger Config
 * @param {String} props.mode Transition Mode
 * @param {String} props.intensity Transition Intensity
 * @param {Number} props.intensityMultiplier Duration Sensivity Factor Default 4
 * @param {String} props.variant Transition Variant
 * @param {Boolean} props.staggerChildren Animation Stagger Config
 * @param {Number} props.delay Animation Delay
 * @param {Boolean} props.isActive Element animating
 * @param {Boolean} props.intersected Element control by intersecting
 * @param {String} props.dataPartial Theme partial
 * @returns {React.ReactElement} MotionMove Component
 * @public
 */
const MotionMove = /*#__PURE__*/(0, _react.forwardRef)(({
  children,
  style,
  className,
  dataCy,
  triggerOnce = false,
  mode = 'fluid',
  intensity = '400',
  intensityMultiplier = 4,
  variant = 'appear',
  staggerChildren = false,
  delay = 0,
  isActive,
  intersected,
  dataPartial
}, forwardedRef) => {
  const containerRef = (0, _react.useRef)(null);
  const {
    ref: inViewRef,
    inView
  } = (0, _reactIntersectionObserver.useInView)({
    triggerOnce: triggerOnce,
    rootMargin: '0px 0px',
    initialInView: false,
    threshold: 0
  });
  const animationRef = !intersected ? inViewRef : containerRef;
  const setRefs = (0, _react.useCallback)(node => {
    if (animationRef) {
      if (typeof animationRef === 'function') {
        animationRef(node);
      } else if (animationRef) {
        animationRef.current = node;
      }
    }
    if (forwardedRef) {
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    }
  }, [animationRef, forwardedRef]);
  (0, _react.useEffect)(() => {
    const container = containerRef.current;
    if (container instanceof HTMLElement) {
      const animationClass = staggerChildren ? 'animate-stagger' : 'animate';
      if (isActive) {
        container.classList.add(animationClass);
      } else {
        container.classList.remove(animationClass);
      }
      return () => {
        container.classList.remove(animationClass);
      };
    }
  }, [isActive, staggerChildren, delay]);
  if (staggerChildren) {
    return /*#__PURE__*/_react.default.createElement(StaggeredContainer, {
      ref: setRefs,
      "data-cy": dataCy || 'motion-move-staggered',
      inView: inView,
      mode: mode,
      intensity: intensity,
      intensityMultiplier: intensityMultiplier,
      variant: variant,
      style: style,
      className: `${inView ? 'animate-stagger' : ''} ${className}`,
      "data-partial": dataPartial
    }, children);
  }
  return /*#__PURE__*/_react.default.createElement(Container, {
    ref: setRefs,
    "data-cy": dataCy || 'motion-move',
    inView: inView,
    mode: mode,
    intensity: intensity,
    intensityMultiplier: intensityMultiplier,
    variant: variant,
    style: style,
    className: `${inView ? 'animate' : ''} ${className}`,
    delay: delay,
    "data-partial": dataPartial
  }, children);
});
var _default = exports.default = MotionMove;
MotionMove.propTypes = {
  children: _propTypes.default.node.isRequired,
  style: _propTypes.default.object,
  className: _propTypes.default.string,
  dataCy: _propTypes.default.string,
  triggerOnce: _propTypes.default.bool,
  mode: _propTypes.default.oneOf(['swift', 'rebound', 'fluid']),
  intensity: _propTypes.default.oneOf(['100', '200', '300', '400', '500']),
  intensityMultiplier: _propTypes.default.number,
  variant: _propTypes.default.oneOf(['appear', 'appearStaggerChildren', 'staggerChildren', 'moveUp', 'moveDown', 'moveLeft', 'moveRight', 'moveUpFixed', 'moveDownFixed', 'moveLeftFixed', 'moveRightFixed', 'scaleDown', 'scaleUp']),
  staggerChildren: _propTypes.default.bool,
  delay: _propTypes.default.number,
  isActive: _propTypes.default.bool,
  intersected: _propTypes.default.bool,
  dataPartial: _propTypes.default.string
};